"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSessionTimeout = useSessionTimeout;
var react_1 = require("react");
var react_redux_1 = require("react-redux");
var auth_1 = require("../features/apis/auth");
var auth_2 = require("../features/slices/auth");
var baseQueryWithReauth_1 = require("../utils/baseQueryWithReauth");
/** When TTE (time-to-expiry) drops below this, consider acting. */
var ACTION_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
/** How recently the user must have interacted to be considered "active". */
var ACTIVE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
/** When inactive and TTE drops below this, show the warning modal. */
var WARNING_WINDOW_MS = 2 * 60 * 1000; // 2 minutes
/** Tick interval for the timer loop. */
var TICK_INTERVAL_MS = 15 * 1000; // 15 seconds
/** Throttle activity timestamp updates to avoid rerenders on every mousemove. */
var ACTIVITY_THROTTLE_MS = 5 * 1000;
var ACTIVITY_EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart", "focus"];
/**
 * Activity-aware session timeout management.
 *
 * Behaviour:
 *  - Listens to mouse/keyboard/scroll events to detect user activity.
 *  - When TTE ≤ 5min AND user was active in the last 5min → silent refresh (no UI).
 *  - When TTE ≤ 2min AND user has been idle ≥ 5min → show warning modal.
 *  - When TTE ≤ 0 → clear credentials and redirect to /login.
 *  - Pauses entirely when the user is not authenticated.
 */
function useSessionTimeout() {
    var _this = this;
    var dispatch = (0, react_redux_1.useDispatch)();
    var _a = (0, react_redux_1.useSelector)(function (s) { return s.auth; }), isAuthenticated = _a.isAuthenticated, expires_at = _a.expires_at;
    var refreshToken = (0, auth_1.useRefreshMutation)()[0];
    var _b = (0, react_1.useState)(false), warningOpen = _b[0], setWarningOpen = _b[1];
    var _c = (0, react_1.useState)(0), secondsRemaining = _c[0], setSecondsRemaining = _c[1];
    // Track activity in a ref so updates don't trigger rerenders.
    var lastActivityRef = (0, react_1.useRef)(Date.now());
    // Track silent-refresh in flight to avoid double calls when ticks overlap.
    var refreshInFlightRef = (0, react_1.useRef)(false);
    // Bind activity listeners
    (0, react_1.useEffect)(function () {
        if (!isAuthenticated)
            return;
        var lastUpdate = 0;
        var onActivity = function () {
            var now = Date.now();
            if (now - lastUpdate < ACTIVITY_THROTTLE_MS)
                return;
            lastUpdate = now;
            lastActivityRef.current = now;
        };
        for (var _i = 0, ACTIVITY_EVENTS_1 = ACTIVITY_EVENTS; _i < ACTIVITY_EVENTS_1.length; _i++) {
            var evt = ACTIVITY_EVENTS_1[_i];
            window.addEventListener(evt, onActivity, { passive: true });
        }
        return function () {
            for (var _i = 0, ACTIVITY_EVENTS_2 = ACTIVITY_EVENTS; _i < ACTIVITY_EVENTS_2.length; _i++) {
                var evt = ACTIVITY_EVENTS_2[_i];
                window.removeEventListener(evt, onActivity);
            }
        };
    }, [isAuthenticated]);
    var silentRefresh = function () { return __awaiter(_this, void 0, void 0, function () {
        var response, stored, parsed, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (refreshInFlightRef.current)
                        return [2 /*return*/, false];
                    refreshInFlightRef.current = true;
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, refreshToken().unwrap()];
                case 2:
                    response = _b.sent();
                    if ((response === null || response === void 0 ? void 0 : response.success) && response.content) {
                        dispatch((0, auth_2.updateToken)({
                            token: response.content.token,
                            expires_at: response.content.expires_at,
                        }));
                        try {
                            stored = localStorage.getItem(baseQueryWithReauth_1.AUTH_STORAGE_KEY);
                            if (stored) {
                                parsed = JSON.parse(stored);
                                parsed.token = response.content.token;
                                parsed.expires_at = response.content.expires_at;
                                localStorage.setItem(baseQueryWithReauth_1.AUTH_STORAGE_KEY, JSON.stringify(parsed));
                            }
                        }
                        catch (_c) {
                            // localStorage write failure is non-fatal
                        }
                        return [2 /*return*/, true];
                    }
                    return [2 /*return*/, false];
                case 3:
                    _a = _b.sent();
                    return [2 /*return*/, false];
                case 4:
                    refreshInFlightRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var forceLogout = function () {
        setWarningOpen(false);
        dispatch((0, auth_2.clearCredentials)());
        localStorage.removeItem(baseQueryWithReauth_1.AUTH_STORAGE_KEY);
        window.location.replace("/login");
    };
    // Main tick loop
    (0, react_1.useEffect)(function () {
        if (!isAuthenticated || !expires_at) {
            setWarningOpen(false);
            return;
        }
        var tick = function () { return __awaiter(_this, void 0, void 0, function () {
            var now, expiresAtMs, tte, idleFor;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        now = Date.now();
                        expiresAtMs = new Date(expires_at).getTime();
                        tte = expiresAtMs - now;
                        idleFor = now - lastActivityRef.current;
                        if (tte <= 0) {
                            forceLogout();
                            return [2 /*return*/];
                        }
                        // While the warning modal is open, just update the countdown.
                        if (warningOpen) {
                            setSecondsRemaining(Math.max(0, Math.floor(tte / 1000)));
                            return [2 /*return*/];
                        }
                        if (!(tte <= ACTION_WINDOW_MS && idleFor < ACTIVE_WINDOW_MS)) return [3 /*break*/, 2];
                        return [4 /*yield*/, silentRefresh()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                    case 2:
                        // Inactive and inside the warning window → ask the user.
                        if (tte <= WARNING_WINDOW_MS && idleFor >= ACTIVE_WINDOW_MS) {
                            setSecondsRemaining(Math.max(0, Math.floor(tte / 1000)));
                            setWarningOpen(true);
                        }
                        return [2 /*return*/];
                }
            });
        }); };
        // Run immediately, then on interval.
        void tick();
        var id = window.setInterval(tick, TICK_INTERVAL_MS);
        return function () { return window.clearInterval(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, expires_at, warningOpen]);
    var extendSession = function () { return __awaiter(_this, void 0, void 0, function () {
        var ok;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, silentRefresh()];
                case 1:
                    ok = _a.sent();
                    setWarningOpen(false);
                    if (!ok) {
                        // Refresh failed — don't trap the user in a stale session.
                        forceLogout();
                    }
                    else {
                        // Reset activity so the modal doesn't re-trigger immediately on the next tick.
                        lastActivityRef.current = Date.now();
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var dismissAndLogout = function () {
        forceLogout();
    };
    return { warningOpen: warningOpen, secondsRemaining: secondsRemaining, extendSession: extendSession, dismissAndLogout: dismissAndLogout };
}
